import { useEffect, useState, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMapEvents,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import markerIconPng from "leaflet/dist/images/marker-icon.png";
import markerShadowPng from "leaflet/dist/images/marker-shadow.png";
import SiteMapElement from "./SiteMapElement";
import FormInputText from "../FormInputText";
import Button from "../button/Button";
import ButtonAction from "../button/ButtonAction";
import CHElement from "../../../model/CHElement";
import MapFitOnEuropeanContinent from "./MapFitOnEuropeanContinent";
import MapFitBounds from "./MapFitBounds";
import MapMarkerData from "../../../model/MapMarkerData";
import ButtonType from "../button/ButtonType";
import ChelementMap from "./ChelementMapElement";
import ChelementMapElement from "./ChelementMapElement";

interface MapDisplayProps {
  data: CHElement[] | undefined;
  isInputEnabled: boolean;
  fitToEurope: boolean;
  onLocationSelect?: (marker: MapMarkerData) => void;
  className?: string;
}

function MapDisplay({
  data,
  isInputEnabled,
  fitToEurope,
  onLocationSelect = () => {},
  className,
}: MapDisplayProps) {
  const [inputMarker, setInputMarker] = useState<MapMarkerData>();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchedAddress, setSearchedAddress] = useState<string | undefined>();
  const mapRef = useRef<L.Map | null>(null);
  // reference used to display automatically the popup with the coordinates
  const inputMarkerRef = useRef(null);

  let mapMarkers: MapMarkerData[] = [];

  // print to console the whole data array
  console.log("MapDisplay data:", data);
  if (data) {
    mapMarkers = data
      .filter((record) => record.latitude != null && record.longitude != null)
      .map((record) => ({
        lat: record.latitude!,
        lon: record.longitude!,
        label: record.name,
        description: record.description,
        info: record,
      }));

        console.log("Markers data:", mapMarkers);
  }

  function AddMarkerOnClick() {
    useMapEvents({
      click(e) {
        if (isInputEnabled) {
          const { lat, lng } = e.latlng;
          setInputMarker({ lat, lon: lng });
          console.log(`Marker Added: Lat: ${lat}, Lon: ${lng}`);
          onLocationSelect(new MapMarkerData(lat, lng));
        }
      },
    });
    return null;
  }

  const searchAddress = async () => {
    if (!searchQuery.trim()) return;

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          searchQuery
        )}`
      );

      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);

      const data = await response.json();

      if (data?.length) {
        const { lat, lon, display_name } = data[0];
        const location = {
          lat: parseFloat(lat),
          lon: parseFloat(lon),
          label: display_name,
        };

        
        if (isInputEnabled) {
          setInputMarker({ lat: location.lat, lon: location.lon });
          console.log(`Marker Added: Lat: ${location.lat}, Lon: ${location.lon}`);
          onLocationSelect(new MapMarkerData(location.lat, location.lon));
        }
        // AddMarkerOnClick();
        setSearchedAddress(display_name);
        mapRef.current?.flyTo([location.lat, location.lon], 15, { duration: 0.8 });
        setTimeout(() => {
          if (inputMarkerRef && inputMarkerRef.current) {
            // @ts-ignore
            inputMarkerRef.current.openPopup();
          }
        }, 300);
      } else {
        alert("Address not found. Please try a different search.");
      }
    } catch (error) {
      console.error("Error fetching geocode data:", error);
      alert("Failed to fetch location. Please check your internet connection.");
    }
  };

  const searchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    console.log(e.key);
    if (e.key === "Enter") {
      e.preventDefault();
      searchAddress();
    }
  };

  // Define the world bounds to prevent panning beyond -180 and 180 longitude
  const worldBounds = L.latLngBounds(
    L.latLng(-90, -180), // Southwest corner
    L.latLng(90, 180) // Northeast corner
  );

  // display automatically the popup with the coordinates
  setTimeout(() => {
    if (inputMarkerRef && inputMarkerRef.current) {
      // @ts-ignore
      inputMarkerRef.current.openPopup();
    }
  }, 200);

  // Runs each time the map component is mounted (displayed in a window)
  useEffect(() => {
    const bounds = L.latLngBounds(
      mapMarkers.map((m) => [m.lat, m.lon])
    );

     mapRef.current?.flyTo([bounds.getCenter().lat, bounds.getCenter().lng], 15, { duration: 0.8 });
  }, []);
  

  return (
    <div
      className={className}
      style={{ display: "flex", flexDirection: "column", width: "100%" }}
    >
      {/* Map Container */}
      <div style={{ flexGrow: 1 }}>
        <MapContainer
          center={[46.774, 23.584]}
          zoom={15}
          style={{ width: "100%", height: "100%" }}
          ref={mapRef}
          worldCopyJump={false} // Prevent jumping to a copied world when panning
          maxBounds={worldBounds} // Restrict panning to the world bounds
          maxBoundsViscosity={1.0} // Enforce the bounds strictly
          minZoom={2}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            noWrap={true}
          />
          <AddMarkerOnClick />

          {inputMarker && (
            <Marker
              position={[inputMarker.lat, inputMarker.lon]}
              ref={inputMarkerRef}
              icon={L.icon({
                iconUrl:
                  "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
                shadowUrl: markerShadowPng,
                iconSize: [25, 41],
                iconAnchor: [12, 41],
              })}
            >
              <Popup autoClose={false} interactive={false} offset={[0, -40]}>
                {searchedAddress && (
                  <div style={{ marginBottom: "6px" }}>
                    <strong>Address:</strong> {searchedAddress}
                  </div>
                )}
                <strong>Lat:</strong> {inputMarker.lat.toFixed(6)}, {" "}
                <strong>Long:</strong> {inputMarker.lon.toFixed(6)}
              </Popup>
            </Marker>
          )}

          {mapMarkers.map((marker, index) => (
            <Marker
              key={index}
              position={[marker.lat, marker.lon]}
              icon={L.icon({
                iconUrl: markerIconPng,
                shadowUrl: markerShadowPng,
                iconSize: [25, 41],
                iconAnchor: [12, 41],
              })}
            >
              {!isInputEnabled && (
                <Popup autoPan={false}>
                  <div
                    style={{
                      width: "min(90vw, 300px)",
                      maxHeight: "60vh",
                      overflowY: "auto",
                    }}
                  >
                    {marker.info && (
                      <ChelementMapElement
                        canEdit={false}
                        data={marker.info}
                        key={marker.info.id}
                      />
                    )}
                  </div>
                </Popup>
              )}
              {isInputEnabled && (
                <Popup autoClose={false} interactive={false} offset={[0, -40]}>
                  <strong>Lat:</strong> {marker.lat.toFixed(6)},{" "}
                  <strong>Long:</strong> {marker.lon.toFixed(6)}
                </Popup>
              )}
            </Marker>
          ))}

          {/* If not fitToEurope */}
          {!fitToEurope ? (
            <MapFitBounds
              markers={mapMarkers}
              mapRef={mapRef}
              recordId={data && data.length > 0 ? data[0].id! : -1}
              inputMarker={inputMarker}
            />
          ) : (
            <MapFitOnEuropeanContinent mapRef={mapRef} />
          )}
        </MapContainer>
      </div>

      {isInputEnabled && (
        <div style={{ marginBottom: "10px", width: "100%" }}>
          <div style={{ display: "flex", gap: "10px", width: "100%" }}>
            <div className="grow">
              <FormInputText
                label=""
                id="search"
                value={searchQuery}
                onChange={(_, value) => setSearchQuery(value)}
                placeholder="Search by address"
                onKeyDown={searchKeyDown}
              />
            </div>
            <Button
              label="Search"
              purpose={ButtonAction.SEARCH}
              type={ButtonType.FILL_ICON_TEXT}
              onClick={searchAddress}
              className="h-11 mt-2"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default MapDisplay;
